import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

const renderParagraphs = (paragraphs = []) =>
  paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>);

const IndustryVerticalPage = ({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  hero,
  overview,
  challenges,
  systems,
  modernization,
  ai,
  experience,
  relatedSolutions,
  why,
  cta,
}) => (
  <React.Fragment>
    <Helmet defer={false}>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {ogImage ? <meta property="og:image" content={ogImage} /> : null}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
    </Helmet>

    <main className="industry-page">
      <section className="industry-hero" aria-labelledby="industry-hero-title">
        <div className="container">
          <div className="industry-hero-grid">
            <div className="industry-hero-copy">
              <span className="section-eyebrow">{hero.eyebrow}</span>
              <h1 id="industry-hero-title">{hero.heading}</h1>
              <p className="industry-hero-lead">{hero.lead}</p>
              <div className="industry-hero-tags" aria-label={`${hero.heading} focus areas`}>
                {hero.tags.map((tag) => (
                  <span key={tag}>{tag}</span>
                ))}
              </div>
              <div className="industry-hero-actions">
                <Link to={hero.primaryCta.href} className="hero-v2-btn-primary">
                  {hero.primaryCta.label}
                </Link>
                <Link to={hero.secondaryCta.href} className="hero-v2-btn-outline">
                  {hero.secondaryCta.label}
                </Link>
              </div>
            </div>

            <div
              className="industry-hero-panel"
              style={{ "--industry-hero-image": `url(${hero.image})` }}
              aria-hidden="true"
            >
              <div className="industry-hero-panel-scrim" />
              <div className="industry-hero-panel-inner">
                <p className="industry-hero-panel-kicker">{hero.panelKicker}</p>
                <h2>{hero.panelTitle}</h2>
                <p>{hero.panelText}</p>
                <div className="industry-hero-stats">
                  {hero.stats.map((stat) => (
                    <div key={stat.label} className="industry-hero-stat">
                      <strong>{stat.value}</strong>
                      <span>{stat.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="industry-section">
        <div className="container">
          <div className="industry-section-heading">
            <p className="section-eyebrow">{overview.eyebrow}</p>
            <h2 className="section-title">{overview.title}</h2>
            <p className="section-sub">{overview.lead}</p>
          </div>
          <div className="row align-items-stretch">
            <div className="col-lg-7 mb-4">
              <div className="industry-copy-card">
                {renderParagraphs(overview.paragraphs)}
              </div>
            </div>
            <div className="col-lg-5 mb-4">
              <div className="industry-info-card industry-info-card--accent">
                <h3>{overview.callout.title}</h3>
                <p>{overview.callout.text}</p>
                <ul className="industry-info-list">
                  {overview.callout.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="industry-section industry-section--muted">
        <div className="container">
          <div className="industry-section-heading">
            <p className="section-eyebrow">{challenges.eyebrow}</p>
            <h2 className="section-title">{challenges.title}</h2>
          </div>
          <div className="row">
            {challenges.items.map((item) => (
              <div key={item.title} className="col-lg-4 mb-4">
                <article className="industry-card">
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="industry-section">
        <div className="container">
          <div className="industry-section-heading">
            <p className="section-eyebrow">{systems.eyebrow}</p>
            <h2 className="section-title">{systems.title}</h2>
            <p className="section-sub">{systems.lead}</p>
          </div>
          <div className="row">
            {systems.items.map((item) => (
              <div key={item.title} className="col-lg-3 col-md-6 mb-4">
                <article className="industry-list-card">
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                  <Link to={item.href} className="industry-inline-link">
                    {item.linkLabel}
                  </Link>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="industry-section industry-section--muted">
        <div className="container">
          <div className="industry-split">
            <div>
              <p className="section-eyebrow">{modernization.eyebrow}</p>
              <h2 className="section-title">{modernization.title}</h2>
              <p className="section-sub">{modernization.lead}</p>
              <div className="industry-copy-card">
                {renderParagraphs(modernization.paragraphs)}
              </div>
            </div>
            <div className="industry-info-card">
              <h3>{modernization.callout.title}</h3>
              <ul className="industry-info-list">
                {modernization.callout.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="industry-section">
        <div className="container">
          <div className="industry-section-heading">
            <p className="section-eyebrow">{ai.eyebrow}</p>
            <h2 className="section-title">{ai.title}</h2>
            <p className="section-sub">{ai.lead}</p>
          </div>
          <div className="row">
            {ai.items.map((item) => (
              <div key={item.title} className="col-lg-4 mb-4">
                <article className="industry-card industry-card--ai">
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              </div>
            ))}
          </div>
          <div className="text-center mt-2">
            <Link to="/enterprise-ai" className="industry-inline-link">
              Explore Enterprise AI
            </Link>
          </div>
        </div>
      </section>

      <section className="industry-section industry-section--muted">
        <div className="container">
          <div className="industry-section-heading">
            <p className="section-eyebrow">{experience.eyebrow}</p>
            <h2 className="section-title">{experience.title}</h2>
          </div>
          <div className="row align-items-stretch">
            <div className="col-lg-7 mb-4">
              <div className="industry-copy-card">
                {renderParagraphs(experience.paragraphs)}
              </div>
            </div>
            <div className="col-lg-5 mb-4">
              <div className="industry-info-card">
                <h3>{experience.callout.title}</h3>
                <ul className="industry-info-list">
                  {experience.callout.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="industry-section">
        <div className="container">
          <div className="industry-section-heading">
            <p className="section-eyebrow">Related Solutions</p>
            <h2 className="section-title">Connect the vertical to the rest of the enterprise story</h2>
          </div>
          <div className="row">
            {relatedSolutions.map((item) => (
              <div key={item.href} className="col-lg-3 col-md-6 mb-4">
                <Link to={item.href} className="industry-link-card">
                  <h3>{item.label}</h3>
                  <p>{item.text}</p>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="industry-section industry-section--muted">
        <div className="container">
          <div className="industry-section-heading">
            <p className="section-eyebrow">{why.eyebrow}</p>
            <h2 className="section-title">{why.title}</h2>
            <p className="section-sub">{why.lead}</p>
          </div>
          <div className="row">
            {why.items.map((item) => (
              <div key={item.title} className="col-lg-3 col-md-6 mb-4">
                <article className="industry-card">
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="industry-cta">
        <div className="container">
          <div className="industry-cta-panel">
            <div>
              <p className="section-eyebrow">{cta.eyebrow}</p>
              <h2>{cta.title}</h2>
              <p>{cta.text}</p>
            </div>
            <div className="industry-cta-actions">
              <Link to={cta.primary.href} className="hero-v2-btn-primary">
                {cta.primary.label}
              </Link>
              <Link to={cta.secondary.href} className="hero-v2-btn-outline">
                {cta.secondary.label}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  </React.Fragment>
);

export default IndustryVerticalPage;
